<template>
    <vx-table-row>
        <vx-table-row-item>
            <template v-slot:label> ID: </template>

            <vx-badge class="vx-cursor-pointer" size="xs" color="slate">
                {{ props.modelValue.id }}
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item class="xl:!vx-col-span-2">
            <template v-slot:label> Name: </template>

            <vx-popover
                hover
                :arrow="true"
                placement="top"
                class="[&>div:first-child]:vx-truncate vx-w-full vx-font-bold"
            >
                {{ props.modelValue.name || '-' }}

                <template v-slot:content>
                    <div class="vx-break-all">{{ props.modelValue.name }}</div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Allocated: </template>

            <vx-icon-loading v-if="loadingStatistics" spin></vx-icon-loading>
            <vx-popover v-else-if="statistics" hover :arrow="true" placement="top">
                <div class="vx-flex vx-items-center vx-gap-2">
                    <div v-if="totalCoupons !== 0" class="vx-w-24 vx-inline-flex vx-gap-0.5 vx-h-2">
                        <div
                            v-if="statistics.num_allocated !== 0"
                            class="vx-rounded-l-md vx-min-w-1"
                            :class="{
                                'vx-rounded-r-md': statistics.num_available === 0,
                                'vx-bg-rose-500': tooManyAllocated,
                                'vx-bg-slate-300': !tooManyAllocated,
                            }"
                            :style="{
                                width: allocatedCouponsPercentage + '%',
                            }"
                        ></div>
                        <div
                            v-if="statistics.num_available !== 0"
                            class="vx-rounded-r-md vx-min-w-1"
                            :class="{
                                'vx-rounded-l-md': statistics.num_allocated === 0,
                                'vx-bg-rose-200': tooManyAllocated,
                                'vx-bg-slate-100': !tooManyAllocated,
                            }"
                            :style="{
                                width: availableCouponsPercentage + '%',
                            }"
                        ></div>
                    </div>
                    <font-awesome-icon
                        v-if="statistics.num_available === 0"
                        :icon="faWarning"
                        class="vx-text-rose-500"
                    ></font-awesome-icon>
                    <span v-if="totalCoupons === 0" class="vx-inline-flex vx-text-xs">No codes yet</span>
                </div>
                <template v-slot:content v-if="totalCoupons">
                    <div v-if="statistics.num_available" class="vx-text-xs">
                        <strong
                            :class="{
                                'vx-text-rose-600': tooManyAllocated,
                            }"
                            >{{ Intl.NumberFormat('en-US').format(statistics.num_allocated) }}</strong
                        >
                        /
                        {{ Intl.NumberFormat('en-US').format(totalCoupons) }}
                    </div>
                    <div v-else class="vx-text-xs">You’ve used all available codes.</div>
                </template>
            </vx-popover>
            <div v-else>-</div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Created at: </template>

            <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                {{ fromNow(props.modelValue.created_at) }}
                <template v-slot:content>
                    {{ shortDateTime(props.modelValue.created_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button type="button" color="muted" outline class="vx-w-[45px]">
                    <font-awesome-icon :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item data-test="delete" @click="destroy()">
                        Delete
                        <font-awesome-icon :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="`/settings/integrations/promotions/${props.modelValue.id}`"
                data-test="edit"
                color="muted"
                outline
            >
                Edit <font-awesome-icon :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faEllipsis, faFilePen, faTrash, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { computed } from 'vue';
import { onMounted, ref } from 'vue';
import { promotionsClient } from '~/services';
import { fromNow, shortDateTime } from '~/utils/date';
import { throwSwalRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const createdAtKey = useInterval(60000);

const emit = defineEmits(['deleted']);

const destroy = () => {
    VxSwal.confirmation('Delete Promotion', 'Are you sure? There is no undo.', 'danger', () =>
        promotionsClient
            .deletePromotion(teamId, props.modelValue.id)
            .then(() => emit('deleted', props.modelValue.id))
            .catch(throwSwalRequestError)
    );
};

const loadingStatistics = ref(false);
const statistics = ref();

const totalCoupons = computed(() =>
    statistics.value ? statistics.value.num_allocated + statistics.value.num_available : 0
);

const allocatedCouponsPercentage = computed(() => {
    if (!totalCoupons.value) {
        return 0;
    }
    return (statistics.value.num_allocated / totalCoupons.value) * 100;
});

const tooManyAllocated = computed(() => allocatedCouponsPercentage.value > 90);

const availableCouponsPercentage = computed(() => {
    if (!totalCoupons.value) {
        return 0;
    }
    return (statistics.value.num_available / totalCoupons.value) * 100;
});

const loadStatistics = async () => {
    try {
        loadingStatistics.value = true;
        const response = await promotionsClient.getPromotionCouponsStatistics(
            props.modelValue.team_id,
            props.modelValue.id
        );
        statistics.value = response.data;
    } finally {
        loadingStatistics.value = false;
    }
};
onMounted(() => {
    loadStatistics();
});
</script>
